import Component from '@glimmer/component';
import DynamicField from '@linc-technologies/linced-common/components/dynamic-field';
import type DynamicFieldsModel from '@linc-technologies/linced-common/models/dynamic-field/base-model';
import type Field from '@linc-technologies/linced-common/models/people-schema/field';
import { hasSavedValue } from '@linc-technologies/linced-common/utils/dynamic-fields';

export interface RequiredFieldsFormSignature {
  Args: {
    // entity is the record that the user completes.
    entity: DynamicFieldsModel;
    // required gives the keys of the fields that the entity must have, in
    // the order to show them.
    required: readonly { key: string }[];
    // schema gives the field definitions. A required field with no
    // definition does not show.
    schema: readonly Field[];
    // parentComponent is the ember-paper form (`form.parentComponent`) to
    // register each field with, so the fields count towards its validity.
    parentComponent?: unknown;
  };
  Element: null;
}

/**
 * RequiredFieldsForm shows a DynamicField for each required field that has no
 * saved value, so the user can complete them.
 *
 * It uses the saved value, not the live value. Thus a field stays while the
 * user types in it, and goes away after a save. A live value check removes
 * the field at the first input event: a date input sends a full date after
 * one digit of the year (HE-351).
 */
export default class RequiredFieldsForm extends Component<RequiredFieldsFormSignature> {
  get fields(): Field[] {
    const { entity, required, schema } = this.args;
    const fields: Field[] = [];
    for (const { key } of required) {
      if (hasSavedValue(entity, key)) {
        continue;
      }
      const field = schema.find((definition) => definition.key === key);
      if (field) {
        fields.push(field);
      }
    }
    return fields;
  }

  <template>
    {{#each this.fields as |field|}}
      <DynamicField
        @entity={{@entity}}
        @field={{field}}
        @parentComponent={{@parentComponent}}
      />
    {{/each}}
  </template>
}
